import kaplay from "kaplay";
import "kaplay/global"; // uncomment if you want to use without the  prefix
import { createClient } from '@supabase/supabase-js'

window.dataLayer = window.dataLayer || []

const hiddenInput = document.getElementById("mobile-keyboard-bridge");
let activeKaplayInput = null;

hiddenInput.addEventListener("input", (e) => {
    if (activeKaplayInput) {
        // Feed the typed text directly into whichever field is active
        activeKaplayInput.text = e.target.value;
    }
});

// 1. Base Project URL (used for fetching public data like leaderboards)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL // <--- MUST end in .supabase.co
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Initialize the client with the root URL
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// 2. Separate Edge Function URL (used strictly for POST submissions)
const edgeFunctionUrl = import.meta.env.VITE_SUPABASE_EDGE_URL
let currentMatchToken = "";
let currentGameSessionId = "";
let matchStartTime = 0;
let totalMovesTracked = 0;
let finalScore = 0;

// Call this when the game ends and they hit "Submit Score" form
async function onScoreSubmit(playerName, playerEmail, finalScore) {
    const elapsedSeconds = (Date.now() - matchStartTime) / 1000;

    const payload = {
        action: 'submit',
        name: playerName,
        email: playerEmail,
        score: finalScore,
        totalMoves: totalMovesTracked,
        durationSeconds: elapsedSeconds,
        matchToken: currentMatchToken,
        gameSessionId: currentGameSessionId
    };

    console.log(payload)

    const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });

    const result = await response.json();
    return result
}

// Call this function the moment the user clicks "Start Game" 
async function onGameStart() {
    finalScore = 0
    totalMovesTracked = 0;
    matchStartTime = Date.now();

    const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'initialize' })
    });
    const data = await response.json();
    
    currentMatchToken = data.matchToken;
    currentGameSessionId = data.gameSessionId;
}

function isValidEmail(email) {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

// Security protection against cross-site scripting (XSS) in player names
function escapeHTML(str) {
    return str.replace(/[&<>'"]/g, 
        tag => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[tag] || tag)
    );
}

kaplay({
  // debugKey: "q", 
  width: 640,
  height: 853,
  pixelDensity: 3,
  letterbox: true,
  font: "semibold"
})

loadRoot("./"); // A good idea for Itch.io publishing later

loadFont("semibold", "fonts/Poppins-SemiBold.ttf")
loadFont("bold", "fonts/Poppins-Bold.ttf")

loadMusic("bgm", "sounds/bgm.ogg")
loadSound("click", "sounds/click.ogg")
loadSound("countdown_beep", "sounds/countdown_beep.ogg")
loadSound("match", "sounds/match.ogg")
loadSound("scene_trans", "sounds/scene_trans.ogg")
loadSound("score_base", "sounds/score_base.ogg")
loadSound("score_final", "sounds/score_final.ogg")
loadSound("swap", "sounds/swap.ogg")
loadSound("times_up", "sounds/times_up.ogg")
loadSound("collect", "sounds/collect.ogg")

loadSprite("logo", "sprites/logo.png")
loadSprite("title", "sprites/title.png")
loadSprite("background", "sprites/background.png")

loadSprite("button_green_big", "sprites/button_green_big.png")
loadSprite("button_green_small", "sprites/button_green_small.png")
loadSprite("button_blue_big", "sprites/button_blue_big.png")
loadSprite("button_blue_small", "sprites/button_blue_small.png")

loadSprite("input_field", "sprites/input_field.png")

loadSprite("panel_basic", "sprites/panel_basic.png", {
  slice9: {
    left: 55,
    top: 55,
    right: 55,
    bottom: 55,
    tileMode: "center"
  }
})
loadSprite("panel_with_header", "sprites/panel_with_header.png", {
  slice9: {
    left: 55,
    top: 82,
    right: 55,
    bottom: 40,
    tileMode: "center"
  }
})
loadSprite("panel_board", "sprites/panel_board.png", {
  slice9: {
    left: 55,
    top: 55,
    right: 55,
    bottom: 55,
    tileMode: "center"
  }
})
loadSprite("slot_time", "sprites/slot_time.png")
loadSprite("slot_cookie", "sprites/slot_cookie.png")
loadSprite("slot_score", "sprites/slot_score.png")

loadSprite("tile_almond", "sprites/tiles/tile_almond.png")
loadSprite("tile_blueberry", "sprites/tiles/tile_blueberry.png")
loadSprite("tile_cashew", "sprites/tiles/tile_cashew.png")
loadSprite("tile_cherry", "sprites/tiles/tile_cherry.png")
loadSprite("tile_honey", "sprites/tiles/tile_honey.png")
loadSprite("tile_oat", "sprites/tiles/tile_oat.png")
loadSprite("tile_cookie", "sprites/tiles/tile_cookie.png")

loadSprite("badge_score", "sprites/badge_score.png", {
  slice9: {
    left: 24,
    top: 24,
    right: 24,
    bottom: 24,
    tileMode: "center"
  }
})

const bgm = play("bgm", {
  volume: 0.8,
  loop: true,
  paused: true
})

const click = play("click", {paused: true})

scene("game", () => {
  bgm.play()
  window.dataLayer.push({
    event: "game_start",
  })

  const BOARD_SIZE = 8
  const TILE_SIZE = 64
  const OFFSET_X = 16 + 8
  const OFFSET_Y = 16 + 8
  const BASE_TILE_SCORE = 5; 
  const TILES = [
    "honey",
    "oat",
    "cherry",
    "blueberry",
    // "cashew",
    "almond",
  ]

  let gameTimeLeft = 60; // Game duration in seconds
  let isGameOver = false;
  let isProcessing = false; // Prevents clicking during cascades

  let score = 0;
  let comboMultiplier = 1;

  let collectiblesCollected = 0;
  const COLLECTIBLE_TILE_IDX = 99;

  // Call this whenever a player makes a match-3 swap move
  function onPlayerMove() {
      totalMovesTracked++;
  }

  // TODO: Uncomment
  onGameStart()

  // ------
  // Init Grid
  // ------

  let grid = []
  let selectedTile = null

  const board = add([
    sprite("panel_board", {width: 560, height: 572}),
    pos(center().sub(vec2(560/2, 572/2)).add(vec2(0, 80))),
  ])

  for (let r = 0; r < BOARD_SIZE; r++) {
      grid[r] = [];
      for (let c = 0; c < BOARD_SIZE; c++) {
          let validTiles = [...Array(TILES.length).keys()];

          // Check left neighbor (prevent 3 horizontal)
          if (c >= 2) {
              let left1 = grid[r][c - 1].tileIdx;
              let left2 = grid[r][c - 2].tileIdx;
              if (left1 === left2) {
                  validTiles = validTiles.filter(idx => idx !== left1);
              }
          }

          // Check top neighbor (prevent 3 vertical)
          if (r >= 2) {
              let top1 = grid[r - 1][c].tileIdx;
              let top2 = grid[r - 2][c].tileIdx;
              if (top1 === top2) {
                  validTiles = validTiles.filter(idx => idx !== top1);
              }
          }

          // Pick a random safe color
          let randTile = validTiles[Math.floor(Math.random() * validTiles.length)];
          grid[r][c] = spawnTile(c, r, randTile);
      }
  }

  function spawnTile(x, y, tileIndex) {

    if (tileIndex === COLLECTIBLE_TILE_IDX) {
      return board.add([
        sprite(`tile_cookie`),
        pos(x * TILE_SIZE + TILE_SIZE / 2 + OFFSET_X, y * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y),
        area(),
        anchor("center"),
        scale(),
        { row: y, col: x, tileIdx: COLLECTIBLE_TILE_IDX, isCollectible: true },
        "tile",
        "collectible"
      ]);
    }

    return board.add([
      sprite(`tile_${TILES[tileIndex]}`),
      // rect(TILE_SIZE - 8, TILE_SIZE - 8, {radius: 8}),
      pos(x * TILE_SIZE + TILE_SIZE / 2 + OFFSET_X, y * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y),
      // color(clr),
      area(),
      anchor("center"),
      scale(),
      { row: y, col: x, tileIdx: tileIndex, isCollectible: false },
      "tile",
    ]);
  }

  // ------
  // Handle Swap & Match
  // ------

  function checkMatches() {
      let matched = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(false));
      let hasMatches = false;

      // Check horizontal matches
      for (let r = 0; r < BOARD_SIZE; r++) {
          for (let c = 0; c < BOARD_SIZE - 2; c++) {
              let c1 = grid[r][c].tileIdx;
              if (c1 === COLLECTIBLE_TILE_IDX) continue;
              let c2 = grid[r][c + 1].tileIdx;
              if (c2 === COLLECTIBLE_TILE_IDX) continue;
              let c3 = grid[r][c + 2].tileIdx;
              if (c3 === COLLECTIBLE_TILE_IDX) continue;
              if (c1 === c2 && c2 === c3) {
                  matched[r][c] = matched[r][c + 1] = matched[r][c + 2] = true;
                  hasMatches = true;
              }
          }
      }

      // Check vertical matches
      for (let c = 0; c < BOARD_SIZE; c++) {
          for (let r = 0; r < BOARD_SIZE - 2; r++) {
              let r1 = grid[r][c].tileIdx;
              if (r1 === COLLECTIBLE_TILE_IDX) continue;
              let r2 = grid[r + 1][c].tileIdx;
              if (r2 === COLLECTIBLE_TILE_IDX) continue;
              let r3 = grid[r + 2][c].tileIdx;
              if (r3 === COLLECTIBLE_TILE_IDX) continue;
              if (r1 === r2 && r2 === r3) {
                  matched[r][c] = matched[r + 1][c] = matched[r + 2][c] = true;
                  hasMatches = true;
              }
          }
      }

      return { hasMatches, matchedMatrix: matched };
  }

  async function checkBottomCollectibles() {
      let tilesToCollect = [];
      let bottomRow = BOARD_SIZE - 1;

      for (let c = 0; c < BOARD_SIZE; c++) {
          let tile = grid[bottomRow][c];
          if (tile && tile.isCollectible) {
              tilesToCollect.push({ tile: tile, col: c });
              grid[bottomRow][c] = null; // Free up the board index immediately
          }
      }

      if (tilesToCollect.length === 0) return false; // Signal that no items were collected

      // Increment global counters
      collectiblesCollected += tilesToCollect.length;
      updateCollectibleUI();

      play("collect", {volume: 0.8})
      // Animate item collection (shrinking and floating downwards out of the board)
      await Promise.all(tilesToCollect.map(item => {
          return Promise.all([
              tween(item.tile.pos.y, item.tile.pos.y + 50, 0.3, (y) => item.tile.pos.y = y, easings.easeInQuad),
              tween(item.tile.scale, vec2(0), 0.3, (s) => item.tile.scale = s, easings.easeInQuad)
          ]).then(() => destroy(item.tile));
      }));

      // Run gravity and refill loops again since removing items leaves empty holes on the bottom row
      await applyGravity();
      await refillBoard();

      return true; // Signal that items were collected, which might require another pass
  }

  // 3. TILE SWAPPING & ROLLBACK
  async function swapTiles(tile1, tile2) {
      // Store original grid index references
      let r1 = tile1.row, c1 = tile1.col;
      let r2 = tile2.row, c2 = tile2.col;

      // Swap data references inside the array
      grid[r1][c1] = tile2;
      grid[r2][c2] = tile1;

      // Update internal tracking parameters
      tile1.row = r2; tile1.col = c2;
      tile2.row = r1; tile2.col = c1;

      play("swap")
      // Visually animate the swap using KAPLAY tweens
      await Promise.all([
          tween(tile1.pos, vec2(c2 * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y, r2 * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y), 0.2, (p) => tile1.pos = p, easings.easeOutQuad),
          tween(tile2.pos, vec2(c1 * TILE_SIZE + TILE_SIZE / 2 + OFFSET_X, r1 * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y), 0.2, (p) => tile2.pos = p, easings.easeOutQuad)
      ]);

      // Check if the move created a matching sequence
      let result = checkMatches();

      if (!result.hasMatches) {
          // Rollback data structures if no match is formed
          grid[r1][c1] = tile1;
          grid[r2][c2] = tile2;
          tile1.row = r1; tile1.col = c1;
          tile2.row = r2; tile2.col = c2;

          play("swap")
          // Animate visual rollback
          await Promise.all([
              tween(tile1.pos, vec2(c1 * TILE_SIZE + TILE_SIZE / 2 + OFFSET_X, r1 * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y), 0.2, (p) => tile1.pos = p, easings.easeOutQuad),
              tween(tile2.pos, vec2(c2 * TILE_SIZE + TILE_SIZE / 2 + OFFSET_X, r2 * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y), 0.2, (p) => tile2.pos = p, easings.easeOutQuad)
          ]);
          isProcessing = false
      } else {
          onPlayerMove() 
          await processMatches(result.matchedMatrix);
          isProcessing = false
      }
  }

  async function processMatches(matchedMatrix) {
      let tilesToDestroy = [];
      let matchPointsThisTurn = 0;
      let matchCenters = []; // Keep track of where to spawn floating text

      for (let r = 0; r < BOARD_SIZE; r++) {
          for (let c = 0; c < BOARD_SIZE; c++) {
              if (matchedMatrix[r][c] && grid[r][c]) {
                  tilesToDestroy.push(grid[r][c]);
                  grid[r][c] = null; 

                  // Calculate value per single tile smashed
                  let tileValue = BASE_TILE_SCORE * comboMultiplier;
                  matchPointsThisTurn += tileValue;
                  
                  // Track coordinates for score pops
                  matchCenters.push({ x: c, y: r, value: tileValue });
              }
          }
      }

      if (tilesToDestroy.length === 0 && !checkBottomCollectibles()) return;

      // Apply calculated points to game state
      score += matchPointsThisTurn;
      updateScoreUI();

      // Spawn localized popups
      matchCenters.forEach(pop => spawnFloatingScore(pop.x, pop.y, pop.value));

      play("match")
      // Animate destruction scale down before deleting
      await Promise.all(tilesToDestroy.map(tile => 
          tween(tile.scale, vec2(0), 0.15, (s) => tile.scale = s, easings.easeInQuad)
              .then(() => destroy(tile))
      ));

      // Shift down remaining items and generate replacements
      await applyGravity();
      await refillBoard();

      let itemsWereCollected = await checkBottomCollectibles();

      // Check if new cascades formed from falling tiles or from collection refills
      let cascadeResult = checkMatches();
      if (cascadeResult.hasMatches || itemsWereCollected) {
          if (cascadeResult.hasMatches) comboMultiplier++;
          // Re-run processing pipeline recursively
          await processMatches(cascadeResult.matchedMatrix);
      }
  }

  async function applyGravity() {
      let tweens = [];

      // Scan columns from bottom to top (excluding top row)
      for (let c = 0; c < BOARD_SIZE; c++) {
          for (let r = BOARD_SIZE - 1; r >= 0; r--) {
              if (grid[r][c] === null) {
                  // Look for the first available tile above the empty hole
                  let lookUpRow = r - 1;
                  while (lookUpRow >= 0 && grid[lookUpRow][c] === null) {
                      lookUpRow--;
                  }

                  // If an upper tile is located, drop it to position 'r'
                  if (lookUpRow >= 0) {
                      let tile = grid[lookUpRow][c];
                      grid[r][c] = tile;
                      grid[lookUpRow][c] = null;

                      tile.row = r; // Sync internal board index reference

                      // Queue animation movement downward
                      tweens.push(
                          tween(
                              tile.pos, 
                              vec2(c * TILE_SIZE + TILE_SIZE / 2 + OFFSET_X, r * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y), 
                              0.5, 
                              (p) => tile.pos = p, 
                              easings.easeOutBounce
                          )
                      );
                  }
              }
          }
      }

      if (tweens.length > 0) {
          await Promise.all(tweens);
      }
  }

  async function refillBoard() {
      let tweens = [];

      for (let c = 0; c < BOARD_SIZE; c++) {
          let missingCount = 0;

          // Count sequential missing spaces starting from top downward
          for (let r = 0; r < BOARD_SIZE; r++) {
              if (grid[r][c] === null) {
                  missingCount++;
                  let randTile
                  if (get("collectible", {recursive: true}).length < 3 && Math.random() < 0.05) { // 10% chance to drop a special item
                      randTile = COLLECTIBLE_TILE_IDX;
                  } else {
                      randTile = Math.floor(Math.random() * TILES.length);
                  }
                  let tile = spawnTile(c, r, randTile);
                  
                  // Position tile off-screen just above the board frame
                  tile.pos = vec2(c * TILE_SIZE + TILE_SIZE / 2 + OFFSET_X, -(missingCount * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y));
                  tile.scale = vec2(0); // Initialize tiny for a scaling entrance effect
                  
                  grid[r][c] = tile;

                  // Animate entry slide-down and scaling pop simultaneously
                  tweens.push(
                      Promise.all([
                          tween(tile.pos, vec2(c * TILE_SIZE + TILE_SIZE / 2 + OFFSET_X, r * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y), 0.3, (p) => tile.pos = p, easings.easeOutQuad),
                          tween(tile.scale, vec2(1), 0.2, (s) => tile.scale = s, easings.easeOutQuad)
                      ])
                  );
              }
          }
      }

      if (tweens.length > 0) {
          await Promise.all(tweens);
      }
  }

  // 4. MOUSE INPUT & SELECTION HANDLING
  onClick("tile", (tile) => {
      if (isGameOver || isProcessing) return;

      if (!selectedTile) {
          selectedTile = tile;
          selectedTile.scale = vec2(1.1)
      } else {
          // Calculate grid distance to check if neighboring
          let dRow = Math.abs(selectedTile.row - tile.row);
          let dCol = Math.abs(selectedTile.col - tile.col);

          selectedTile.scale = vec2(1)

          if ((dRow === 1 && dCol === 0) || (dRow === 0 && dCol === 1)) {
              let t1 = selectedTile;
              selectedTile = null;
              isProcessing = true
              comboMultiplier = 1
              swapTiles(t1, tile);
          } else {
              // If clicked too far, change selection to new tile
              selectedTile = tile;
              selectedTile.scale = vec2(1.1)
          }
      }
  });

  function makeScoreSlot(_slot, _pos, _text) {
    const slot = add([
      sprite(_slot),
      anchor("top"),
      pos(_pos)
    ])
    const label = slot.add([
        text(_text, { font: "bold", size: 32, align: "center" }),
        pos(0, 72), 
        anchor("center"),
        color(BLACK),
        scale()
    ]);

    return label
  }

  // ------
  // Timer UI
  // ------
  const timerLabel = makeScoreSlot("slot_time", vec2(width()/2 - 190, 90), gameTimeLeft)

  // ------
  // Collectibles UI
  // ------
  const collectibleLabel = makeScoreSlot("slot_cookie", vec2(width()/2 - 45, 90), collectiblesCollected)

  // ------
  // Score UI
  // ------
  const scoreLabel = makeScoreSlot("slot_score", vec2(width()/2 + 145, 90), score)

  function updateScoreUI() {
      scoreLabel.text = score;
      
      // Quick punch animation when score updates
      tween(scoreLabel.scale, vec2(1.2), 0.05, (s) => scoreLabel.scale = s, easings.easeOutQuad)
          .then(() => tween(scoreLabel.scale, vec2(1), 0.1, (s) => scoreLabel.scale = s, easings.easeInQuad));
  }

  function spawnFloatingScore(x, y, value) {
      const popup = board.add([
          text(`+${value}`, { size: 24}),
          pos(x * TILE_SIZE + TILE_SIZE / 2 + OFFSET_X, y * TILE_SIZE + TILE_SIZE / 2 + OFFSET_Y),
          color(255, 255, 255),
          anchor("center"),
          scale(),
          opacity(),
          z(10), // Render on top of tiles
      ]);

      // Animate up and fade away
      tween(popup.pos.y, popup.pos.y - 40, 0.6, (yPos) => popup.pos.y = yPos, easings.easeOutQuad);
      tween(popup.opacity, 0, 0.6, (o) => popup.opacity = o, easings.easeOutQuad)
          .then(() => destroy(popup));
  }


  function updateCollectibleUI() {
      collectibleLabel.text = collectiblesCollected;
  }


  loop(1, () => {
      if (isGameOver || isProcessing) return;

      gameTimeLeft--;
      timerLabel.text = gameTimeLeft;

      // Visual warning shake when time gets low (10 seconds or less)
      if (gameTimeLeft <= 10 && gameTimeLeft > 0) {
          play("countdown_beep")
          timerLabel.color = rgb(255, 0, 0);
          tween(timerLabel.scale, vec2(1.7), 0.1, (s) => timerLabel.scale = s)
              .then(() => tween(timerLabel.scale, vec2(1), 0.1, (s) => timerLabel.scale = s));
      }

      if (gameTimeLeft <= 0) {
          play("times_up")
          isGameOver = true
          wait(2).then(() => {
            go("gameover", {score: score, collectibles: collectiblesCollected})
          })
      }
  });

  onDraw(() => {
    drawSprite({
      sprite: "background"
    })

    drawSprite({
      sprite: "logo",
    })
  })

})

function makePanel(_pos, _width, _height, _delay = 0.3) {
  const panel = add([
    sprite("panel_basic", {width: _width, height: _height}),
    pos(_pos.x, _pos.y+100),
    anchor("top"),
    scale(0),
    {
      contentY: 10,
      add() {
        wait(_delay).then(() => {
          tween(this.pos, _pos, 0.2, (p) => this.pos = p, easings.easeOut)
          tween(this.scale, vec2(1), 0.2, (p) => this.scale = p, easings.easeOutQuad)
        })
      } 
    }
  ])

  return panel
}

function makePanelWithHeader(_header, _pos, _width, _height, _delay = 0.3) {
  const panel = add([
    sprite("panel_with_header", {width: _width, height: _height}),
    pos(_pos.x, _pos.y+100),
    anchor("top"),
    scale(0),
    {
      contentY: 110,
      add() {
        wait(_delay).then(() => {
          tween(this.pos, _pos, 0.2, (p) => this.pos = p, easings.easeOut)
          tween(this.scale, vec2(1), 0.2, (p) => this.scale = p, easings.easeOutQuad)
        })
      } 
    }
  ])

  panel.add([
    text(_header, {font: "semibold", size: 36, align: "center"}),
    anchor("center"),
    pos(0, 38)
  ])

  return panel
}

function makeListItem(_parent, _rank, _name, _score, _posY) {
  const listItem = _parent.add([
    rect(434, 68, {fill: false}),
    anchor("center"), 
    pos(0, _posY)
  ])
  
  const rank = listItem.add([
    text(_rank, {font: "semibold", size: 21, align: "center"}),
    anchor("left"),
    pos(-listItem.width / 2, 0),
    color(BLACK),
  ])

  listItem.add([
    text(_name, {font: "semibold", size: 27}),
    anchor("left"),
    pos(rank.pos.x + rank.width + 20, 0),
    color(BLACK),
  ])

  listItem.add([
    text(_score, {font: "semibold", size: 27, align: "right"}),
    anchor("right"),
    pos(listItem.width / 2, 0),
    color(BLACK),
  ])

  return listItem
}

scene("leaderboard", () => {
  bgm.play()
  let isLoading = true

  const leaderboardPanel = makePanelWithHeader("Leaderboard", vec2(width()/2, 80), 490, 450, 0.1)
  const playerInfoPanel = makePanel(vec2(width()/2, 80 + 450 + 3), 490, 116, 0.2)

  let email = localStorage.getItem("email")

  const playerInfoText = playerInfoPanel.add([
    text(email ? "[def]Getting your scores[/def][wiggle]...[/wiggle]" : "[def]You don't have scores yet.[/def]", {
      font: "semibold", 
      size: 27, 
      align: "center",
      styles: {
        "def": {
          color: BLACK
        },
        "awesome": (idx, ch) => ({
          color: Color.fromHex("#F34271"),
          pos: vec2(0, wave(-2, 2, time() * 4 + idx * 0.3)),
          font: "bold"
        }),
        "wiggle": (idx, ch) => ({
          pos: vec2(0, wave(0, 4, time() * 4 + idx * 1.4)),
        }) 

      }
    }),
    pos(0, playerInfoPanel.contentY + 30),
    anchor("top"),
  ])

  const loadingText = leaderboardPanel.add([
    text("Getting latest scores[wiggle]...[/wiggle]", {font: "semibold", size: 27, align: "center",
      styles: {
        "wiggle": (idx, ch) => ({
          pos: vec2(0, wave(0, 4, time() * 4 + idx * 1.4)),
        }) 
      }
    }),
    pos(0, leaderboardPanel.contentY + 50),
    anchor("top"),
    color(BLACK)
  ])
  
  displayLeaderboard()
  if (email) displayPersonalRank(email)

  async function displayLeaderboard() {
      // SECURE: We ONLY ask for 'name' and 'high_score'. 
      // The 'email' column remains completely locked inside the database.
      const { data: records, error } = await supabase
          .from(import.meta.env.VITE_SUPABASE_MAIN_TABLE) // Using your live table name
          .select('name, high_score') 
          .order('high_score', { ascending: false })
          .limit(5);

      if (error) {
          console.error('Error fetching leaderboard:', error);
          loadingText.text = 'Failed to load scores.\nCome back again later.';
          return;
      }

      if (records.length === 0) {
          loadingText.text = 'No high scores yet.\nBe the first!';
          return;
      }

      // Loop through and render the safe data
      records.forEach((player, index) => {
          const rank = index + 1;
          let medal = rank;
          if (rank === 1) medal = '🥇';
          else if (rank === 2) medal = '🥈';
          else if (rank === 3) medal = '🥉';

          makeListItem(leaderboardPanel, rank, player.name, player.high_score, leaderboardPanel.contentY + index * 68) 

      });

      isLoading = false

      destroy(loadingText)
  }

  async function displayPersonalRank(email) {
      // SECURE: We ONLY ask for 'name' and 'high_score'. 
      // The 'email' column remains completely locked inside the database.
      const { data, error } = await supabase
        .from(import.meta.env.VITE_SUPABASE_LEADERBOARD_TABLE)
        .select('name, high_score, rank')
        .eq('email', email)
        .single();

      if (error) {
          console.error('Error fetching personal rank:', error);
          playerInfoText.text = 'Failed to load score. Come back again later.';
          return;
      }

      if (!data) {
          playerInfoText.text = 'You don\'t have scores yet';
          return;
      }

      if (data.rank <= 5) {
        playerInfoText.text = `[def]Wohoo! You’re the[/def] [awesome]Top ${data.rank}![/awesome]`
        return
      }

      makeListItem(playerInfoPanel, data.rank, data.name, data.high_score, playerInfoPanel.contentY + 42) 
      destroy(playerInfoText)
  }

  const backBtn = makeButton("Home", vec2(width()/2 - 130, height()/2 + 290), "blue", "small", 0.3)

  backBtn.onClick(() => {
    click.play()
    go("menu")
  })

  const playBtn = makeButton("Play", vec2(width()/2 + 130, height()/2 + 290), "green", "small", 0.4)

  playBtn.onClick(() => {
    click.play()
    go("objective")
  })

  onDraw(() => {
    drawSprite({
      sprite: "background"
    })

    drawSprite({
      sprite: "logo",
    })
  })
})

function makeButton(_text, _pos, _color, _size, _delay = 0.3) {
  const btn = add([
    sprite(`button_${_color}_${_size}`),
    anchor("center"),
    pos(_pos.x, _pos.y),
    scale(0),
    area(),
    {
      add() {
        wait(_delay).then(() => {
          tween(this.pos, _pos, 0.2, (p) => this.pos = p, easings.easeOut)
          tween(this.scale, vec2(1), 0.2, (p) => this.scale = p, easings.easeOutQuad)
        })
      } 
    }
  ])

  btn.add([
      text(_text, { font: "semibold", size: 33, align: "center" }),
      pos(0, -5),
      anchor("center"),
      color(WHITE),
  ]);

  return btn
}


scene("menu", () => {
  bgm.play()
  const startBtn = makeButton("Play", vec2(width()/2, height()/2 + 130), "green", "big", 0.1)
  startBtn.onClick(() => {
    click.play()
    go("objective")
  }) 

  const leaderboardBtn = makeButton("Leaderboard", vec2(width()/2, startBtn.pos.y + 120), "blue", "big", 0.2)
  leaderboardBtn.onClick(() => {
    click.play()
    go("leaderboard")
  }) 

  onDraw(() => {
    drawSprite({
      sprite: "background"
    })

    drawSprite({
      sprite: "logo",
    })

    drawSprite({
      sprite: "title",
      pos: vec2(width() / 2, 200),
      anchor: "center"
    })

    drawText({
      text: "Play for a chance to win\n[prize]Mini Granola Packs (4oz)[/prize]\nat Chicagofest 2026!",
      width: 538,
      font: "semibold",
      size: 30,
      align: "center",
      lineSpacing: 12,
      styles: {
        "prize": (idx, ch) => ({
          color: Color.fromHex("#FFC718"),
          pos: vec2(0, wave(-2, 2, time() * 4 + idx * 0.3)),
          font: "bold"
        }),
      },
      pos: vec2(width() / 2, height() / 2 - 20),
      anchor: "center"
    })
  })

})

function makeScoreBadge(_parent, _title, _text, _pos, _size = "s") {
  const w = _size === "s" ? 96 : (_size === "m" ? 116 : 151)

  const badge = _parent.add([
    sprite("badge_score", {width: w, height: 43}),
    anchor("center"),
    pos(_pos)
  ])

  badge.add([
    text(_title, {size: 16}),
    anchor("center")
  ])

  const label = badge.add([
    text(_text, {font: "bold", size: 28, align: "center"}),
    anchor("center"),
    pos(0, 50),
    color(BLACK),
    scale()
  ])

  return {badge, label}
}

scene("gameover", ({score, collectibles}) => {
  const finalScore = score + collectibles * 1000
  
  window.dataLayer.push({
    event: "game_complete",
    game_score: finalScore,
  })

  const gameDataPanel = makePanelWithHeader("Time's up!", vec2(width()/2, 150), 490, 250, 0.1)

  const {badge: scoreBadge, label: scoreLabel} = makeScoreBadge(gameDataPanel, "Score", score, vec2(-160, gameDataPanel.contentY + 10)) 

  const {badge: collectibleBadge, label: collectibleLabel} = makeScoreBadge(gameDataPanel, "Cookies", collectibles, vec2(-30, gameDataPanel.contentY + 10), "m") 

  const {badge: finalBadge, label: finalLabel} = makeScoreBadge(gameDataPanel, "Final score", 0, vec2(130, gameDataPanel.contentY + 10), "l") 

  Promise.all([
    tween(finalLabel.text, score, 0.8, (p) => finalLabel.text = Math.floor(p), easings.easeOutQuad),
    tween(scoreLabel.scale, vec2(1.3), 0.5, (p) => scoreLabel.scale = p, easings.easeOutQuad),
    tween(finalLabel.scale, vec2(1.3), 0.5, (p) => finalLabel.scale = p, easings.easeOutQuad),
    play("score_base")
  ])
    .then(() => {
      tween(scoreLabel.scale, vec2(1), 0.8, (p) => scoreLabel.scale = p, easings.easeOutQuad)
      tween(finalLabel.scale, vec2(1), 0.5, (p) => finalLabel.scale = p, easings.easeOutQuad)
        .then(() => {
          Promise.all([
            tween(finalLabel.text, finalScore, 0.8, (p) => finalLabel.text = Math.floor(p), easings.easeOutQuad),
            tween(collectibleLabel.scale, vec2(1.3), 0.5, (p) => collectibleLabel.scale = p, easings.easeOutQuad),
            tween(finalLabel.scale, vec2(1.3), 0.5, (p) => finalLabel.scale = p, easings.easeOutQuad),
            play("score_final")
          ])
            .then(() => {
              tween(collectibleLabel.scale, vec2(1), 0.8, (p) => collectibleLabel.scale = p, easings.easeOutQuad)
              tween(finalLabel.scale, vec2(1), 0.5, (p) => finalLabel.scale = p, easings.easeOutQuad)
            })
        })
    
    })


  const playerInfoPanel = makePanel(vec2(width()/2, 150 + 250 + 3), 490, 168, 0.2)

  const playerInfoText = playerInfoPanel.add([
    text("", {
      font: "semibold", 
      size: 27, 
      width: 414,
      align: "center",
      styles: {
        "def": {
          color: BLACK
        },
        "awesome": (idx, ch) => ({
          color: Color.fromHex("#F34271"),
          pos: vec2(0, wave(-2, 2, time() * 4 + idx * 0.3)),
          font: "bold"
        }) 
      }
    }),
    pos(0, playerInfoPanel.contentY + 30),
    anchor("top"),
  ])

  let email = localStorage.getItem("email")
  let message = ""

  if (!email) {
    playerInfoText.text = "[def]Save your score for a chance to win[/def] [awesome]Mini Granola Packs.[/awesome]"
    playerInfoText.pos = vec2(0, playerInfoPanel.contentY + 40)
  } else {
    getPersonalScore(email).then(({success, highscore}) => {
      if (!success) return

      if (finalScore > highscore) {
        playerInfoText.text = "[awesome]You got a new highscore![/awesome]\n[def]Save your score for a chance to win Mini Granola Packs![/def]"
      }
      else {
        playerInfoText.text = `[def]You can beat your personal best at[/def] [awesome]${highscore}[/awesome][def]![/def]`
        playerInfoText.pos = vec2(0, playerInfoPanel.contentY + 40)
      }
    })
  }
  

  const saveBtn = makeButton("Save score", vec2(width()/2 - 130, 403 + 168 + 60), "blue", "small", 0.3)

  saveBtn.onClick(() => {
    click.play()
    let name = localStorage.getItem("name")
    if(!name || !email) {
      // scoreFormDialog.showModal()
      go("save_score", {finalScore})
    }
    else {
      go("processing_save", {name, email, finalScore})
      // await onScoreSubmit(name, email, finalScore)
      // go("leaderboard")
    }
  })

  const playBtn = makeButton("Retry", vec2(width()/2 + 130, 403 + 168 + 60), "green", "small", 0.4)

  playBtn.onClick(() => {
    click.play()
    go("objective")
  })

  // showFormBtn.onClick(async () => {
  //   let name = localStorage.getItem("name")
  //   let email = localStorage.getItem("email")
  //   if(!name || !email) {
  //     scoreFormDialog.showModal()
  //   }
  //   else {
  //     await onScoreSubmit(name, email, finalScore)
  //     go("leaderboard")
  //   }
  // }) 

  onDraw(() => {
    drawSprite({
      sprite: "background"
    })

    drawSprite({
      sprite: "logo",
    })
  })

  async function getPersonalScore(email) {
      // SECURE: We ONLY ask for 'name' and 'high_score'. 
      // The 'email' column remains completely locked inside the database.
      const { data, error } = await supabase
        .from(import.meta.env.VITE_SUPABASE_MAIN_TABLE)
        .select('high_score')
        .eq('email', email)
        .single();

      if (error) {
        return {success: 0, highscore: null};
      }

      if (!data) {
        return {success: 0, highscore: null};
      }

      return {success: 1, highscore: data.high_score};
  }

})

function makeInput(_parent, _label, _type, _pos, _inputType = "text") {
  const inputField = _parent.add([
    sprite("input_field"),
    anchor("top"),
    pos(_pos),
    area(),
  ])

  inputField.add([
    text(_label, {size: 18}),
    anchor("center"),
    pos(0, 23)
  ])

  const input = inputField.add([
    text("", {size: 28, align: "center"}),
    textInput(false),
    color(BLACK),
    anchor("center"),
    pos(0, 68),
    _type
  ])

  inputField.onClick(() => {
    activeKaplayInput = input;
    input.focus();
    
    // Configure keyboard for normal text layout
    hiddenInput.inputMode = _inputType; 
    hiddenInput.value = input.text;
    hiddenInput.focus();    
  })

  return {inputField, input}
}

scene("save_score", ({finalScore}) => {
  const formPanel = makePanelWithHeader("Save score", vec2(width()/2, 100), 490, 475, 0.1)

  const {inputField: nameField, input: nameInput} = makeInput(formPanel, "Name", "name", vec2(0, 100))
  const {inputField: emailField, input: emailInput} = makeInput(formPanel, "Email", "email", vec2(0, nameField.pos.y + 125), "email")

  const agreement = formPanel.add([
    text("[def]By saving your score, you thereby agree to our[/def] [pink]Terms & Conditions[/pink] [def]and[/def] [pink]Privacy Policy[/pink][def].[/def]", {
      size: 18,
      align: "center",
      lineSpacing: 8,
      width: 400,
      styles: {
        "def": {
          color: BLACK
        },
        "pink": {
          color: Color.fromHex("#F34271")
        }
      }
    }),
    anchor("top"),
    pos(0, emailField.pos.y + 140)
  ])

  const tncLink = agreement.add([
    area({shape: new Rect(vec2(-165,26), 185, 18)}),
  ])
  tncLink.onClick(() => window.open(import.meta.env.VITE_TERMS_URL, '_blank'))

  const ppLink = agreement.add([
    area({shape: new Rect(vec2(63,26), 132, 18)}),
  ])
  ppLink.onClick(() => window.open(import.meta.env.VITE_POLICY_URL, '_blank'))

  const skipBtn = makeButton("Skip", vec2(width()/2 - 130, height()/2 + 210), "blue", "small", 0.2)

  skipBtn.onClick(() => {
    click.play()
    go("menu")
  })

  const saveBtn = makeButton("Save", vec2(width()/2 + 130, height()/2 + 210), "green", "small", 0.3)

  saveBtn.onClick(() => {
    click.play()
    if (!nameInput.text || !emailInput.text) return
    if (!isValidEmail(emailInput.text)) return
    
    go("processing_save", {finalScore, name: escapeHTML(nameInput.text.trim()), email: emailInput.text.trim()})
  })

  onMousePress(() => {
    if (!nameField.isHovering() && !emailField.isHovering()) {
        nameInput.hasFocus = false
        emailInput.hasFocus = false
        activeKaplayInput = null;
        hiddenInput.blur(); // Hides mobile keyboard
    }
  })

  onDraw(() => {
    drawSprite({
      sprite: "background"
    })

    drawSprite({
      sprite: "logo",
    })
  })
})

scene("processing_save", ({finalScore, name, email}) => {
  const infoPanel = makePanelWithHeader("Save score", vec2(width()/2, height()/2 - 120), 490, 192, 0.1)

  infoPanel.add([
    text("Saving your score[wiggle]...[/wiggle]", {
      size: 33,
      align: "center",
      styles: {
        "wiggle": (idx, ch) => ({
          pos: vec2(0, wave(0, 4, time() * 4 + idx * 1.4)),
        }) 
      }
    }),
    color(BLACK),
    anchor("top"),
    pos(0, 100)
  ])

  onScoreSubmit(name, email, finalScore)
    .then((result) => {
      if(result.success) {
        localStorage.setItem("name", name)
        localStorage.setItem("email", email)
        go("save_successful") 
      }
      else {
        go("save_failed", {name, email, finalScore})  
      }
    })

  onDraw(() => {
    drawSprite({
      sprite: "background"
    })

    drawSprite({
      sprite: "logo",
    })
  })
})

scene("save_successful", () => {
  window.dataLayer.push({
    event: "score_submit",
  })

  play("scene_trans")

  const infoPanel = makePanelWithHeader("Save score", vec2(width()/2, height()/2 - 230), 490, 235, 0.1)

  infoPanel.add([
    text("Horray — You’re now on the leaderboard!", {
      size: 33,
      align: "center",
      lineSpacing: 12,
      width: 434
    }),
    color(BLACK),
    anchor("top"),
    pos(0, 100)
  ])

  const startBtn = makeButton("Play again", vec2(width()/2, height()/2 + 80), "green", "big", 0.2)
  startBtn.onClick(() => {
    click.play()
    go("objective")
  }) 

  const leaderboardBtn = makeButton("Leaderboard", vec2(width()/2, startBtn.pos.y + 120), "blue", "big", 0.3)
  leaderboardBtn.onClick(() => {
    click.play()
    go("leaderboard")
  }) 

  onDraw(() => {
    drawSprite({
      sprite: "background"
    })

    drawSprite({
      sprite: "logo",
    })
  })
})

scene("save_failed", ({name, email, finalScore}) => {
  play("scene_trans")
  
  const infoPanel = makePanelWithHeader("Save score", vec2(width()/2, height()/2 - 230), 490, 278, 0.1)

  infoPanel.add([
    text("Uh-oh... there’s a problem while saving your score.", {
      size: 33,
      align: "center",
      lineSpacing: 12,
      width: 434
    }),
    color(BLACK),
    anchor("top"),
    pos(0, 100)
  ])

  const skipBtn = makeButton("Skip", vec2(width()/2 - 130, height()/2 + 110), "blue", "small", 0.2)

  skipBtn.onClick(() => {
    click.play()
    go("menu")
  })

  const retryBtn = makeButton("Retry", vec2(width()/2 + 130, height()/2 + 110), "green", "small", 0.3)

  retryBtn.onClick(() => {
    click.play()
    go("processing_save", {name, email, finalScore})
  })

  onDraw(() => {
    drawSprite({
      sprite: "background"
    })

    drawSprite({
      sprite: "logo",
    })
  })
})

scene("objective", () => {
  bgm.play()
  const infoPanel = makePanelWithHeader("Objective", vec2(width()/2, height()/2 - 230), 490, 278, 0.1)

  infoPanel.add([
    text("Match as many as you can \nin 60s. Collect cookies at the bottom to get extra points!", {
      size: 28,
      align: "center",
      lineSpacing: 12,
      width: 434
    }),
    color(BLACK),
    anchor("top"),
    pos(0, 110)
  ])

  const startBtn = makeButton("Got it!", vec2(width()/2, height()/2 + 120), "green", "big", 0.25)
  startBtn.onClick(() => {
    click.play()
    go("game")
  }) 

  onDraw(() => {
    drawSprite({
      sprite: "background"
    })

    drawSprite({
      sprite: "logo",
    })
  })
})

// go("gameover", {score: 12300, collectibles: 3})
// go("save_score", {finalScore: 101010})
// go("processing_save", {finalScore: 101010})
// go("save_successful")
// go("objective")
go("menu")
